import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";

import {
  Monitor,
  Database,
  HardDrive,
  ChevronRight,
  Search,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  User,
  Cloud,
  Clock,
  Server,
} from "lucide-react";
import { SiGooglecloud, SiAmazon, SiKubernetes } from "react-icons/si";

import {
  Resource,
  projects,
  getGCPVMList,
  getGCPStorageList,
  getGCPDatabaseList,
  getGCPUserProjectAccess,
  getAWSKubernetesList,
  getAWSUserProjectAccess,
  getAzureVMList,
  getAzureStorageList,
  getAzureDatabaseList,
  getAzureKubernetesList,
  getAzureUserProjectAccess,
  getAWSEC2List,
  getAWSS3List,
  getAWSRDSList,
  getGCPKubernetesList,
  getGCPRolesList,
  getAzureUserConfig,
  getGCPUserConfig,
  getAzureRoleList,
  UserProjectAccess,
  getResourceTypes,
  getAWSPoliciesList,
  formatTime,
} from "./Data";
import { DetailsDialog } from "../Cloud/DetailsDialog";
import RequestCloudAccessDialog from "./RequestCloudAccessDialog";
import { CloudUsersPageProps } from "../Cloud/interface";
import CloudAccountList from "../CloudAccountList";
import TenantCloudConfigureDialog from "./TenantCloudConfigureDialog";
import useUserStore from "../../../services/userStore";
import useNotificationsStore from "../../../services/notificationStore";
import { configureGCPUser } from "./Api/tenantgcpapi";
import { myLocalStorage } from "../../StorageHelper";
import { configureUser } from "./Api/tenantazureapi";
import { FaGoogle } from "react-icons/fa6";
import { VscAzure } from "react-icons/vsc";
import { configureAWSUser, fetchUserConfiguration } from "./Api/tenantawsapi";

const ITEMS_PER_PAGE = 12;



const cloudConfig: Record<string, { icon: any; name: string; fullName: string; label: string }> = {
  gcp: { icon: FaGoogle, name: "GCP", fullName: "Google Cloud Platform", label: "GCP" },
  aws: { icon: SiAmazon, name: "AWS", fullName: "Amazon Web Services", label: "AWS" },
  azure: { icon: VscAzure, name: "Azure", fullName: "Microsoft Azure", label: "Azure" },
};


export default function TenantCloudResources({ cloudProvider }: CloudUsersPageProps) {
  const [selectedTenant, setSelectedTenant] = useState("");

  const [selectedResourceType, setSelectedResourceType] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeSearchQuery, setTypeSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [requestAccessDialogOpen, setRequestAccessDialogOpen] = useState(false);
  const [requestAccessResource, setRequestAccessResource] = useState<Resource | null>(null);
  const [cloudProviderChanges, setCloudProviderChanges] = useState<any>();
  const [currentResources, setCurrentResources] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [isConfigured, setIsConfigured] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [cloudConfigData, setCloudConfigData] = useState<any>({
    azureDisplayName: "",
    azureId: "",
    gcpUserName: "",
    gcpAccountEmail: "",
    awsCredentials: [], // { id, username, arn }
  });
  const [userId, setUserId] = useState("");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [projectId, setProjectId] = useState("");
  const { email } = useUserStore((state) => state.user);
  const [userAccess, setUserAccess] = useState<UserProjectAccess | null>(null);
  const { userAzureApprovedNotifications, userGCPApprovedNotifications, userAWSApprovedNotifications } = useNotificationsStore();
  const [refreshAccountList, setRefreshAccountList] = useState(0);

  // Timer for logan-ubuntu-unsafe2 - 30 minutes countdown
  const [remainingSeconds, setRemainingSeconds] = useState(30 * 60); // 30 minutes in seconds


  const config = cloudConfig[cloudProvider];
  const CloudIcon = config.icon;

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);



  const handleCloudProvider = (value: any) => {
    setCloudProviderChanges(value);
  };


  const getCurrentProjectResources = useCallback(async () => {
    if (cloudProvider === "gcp") {
      const [vmList, storageList, dbList, kubernetes] = await Promise.all([
        getGCPVMList(),
        getGCPStorageList(),
        getGCPDatabaseList(),
        getGCPKubernetesList()
      ]);
      return [...vmList, ...storageList, ...dbList, ...kubernetes];
    } else if (cloudProvider === "aws") {
      const [vmList, storageList, dbList, kubernetes] = await Promise.all([
        getAWSEC2List(),
        getAWSS3List(),
        getAWSRDSList(),
        getAWSKubernetesList()
      ]);
      return [...vmList, ...storageList, ...dbList, ...kubernetes];
    } else {
      const [vmList, storageList, dbList, kubernetes] = await Promise.all([
        getAzureVMList(),
        getAzureStorageList(),
        getAzureDatabaseList(),
        getAzureKubernetesList(),
      ]);
      return [...vmList, ...storageList, ...dbList, ...kubernetes];
    }
  }, [cloudProvider, selectedTenant, selectedResourceType, cloudProviderChanges]);


  const getApplicableRoles = useCallback(async () => {
    if (selectedResourceType) {
      if (cloudProvider === "gcp") {
        const applicableRoles = await getGCPRolesList(selectedResourceType || "");
        return [...applicableRoles]
      } else if (cloudProvider === "azure") {
        if (requestAccessResource) {
          const applicableRoles = await getAzureRoleList(selectedResourceType || "", userId, requestAccessResource?.id);
          return [...applicableRoles]
        }
      } else if (cloudProvider === "aws") {
        const applicableRoles = await getAWSPoliciesList();
        return [...applicableRoles]
      }
    }

    return [];
  }, [cloudProvider, selectedTenant, requestAccessDialogOpen, selectedResourceType])



  useEffect(() => {
    const fetchUserAccess = async () => {
      let result: UserProjectAccess | null = null;

      if (cloudProvider === "gcp" && projectId && userEmail) {
        result = await getGCPUserProjectAccess(projectId, userEmail);
      } else if (cloudProvider === "aws" && userEmail) {
        result = await getAWSUserProjectAccess(userEmail);
      } else if (cloudProvider === "azure" && userId) {
        result = await getAzureUserProjectAccess(userId);
      } else {
        return;
      }

      setUserAccess(result);
    };

    fetchUserAccess();
  }, [cloudProvider, userId, projectId, userEmail, cloudProviderChanges]);

  const filteredResourceTypes = getResourceTypes(cloudProvider).filter((type) =>
    type.name.toLowerCase().includes(typeSearchQuery.toLowerCase())
  );

  const typeFilteredResources = selectedResourceType
    ? currentResources.filter((r) => r.type === selectedResourceType)
    : currentResources;

  const filteredResources = searchQuery
    ? typeFilteredResources.filter((r) =>
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.region.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : typeFilteredResources;

  const totalPages = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResources = filteredResources.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleFilterChange = (type: string | null) => {
    setSelectedResourceType(type);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const getResourceTypeIcon = (type: string) => {
    const resourceType = getResourceTypes(cloudProvider).find((rt) => rt.id === type);
    return resourceType || getResourceTypes(cloudProvider)[0];
  };

  const fetchAccessGranted = (type: string, resource: any) => {

    if (cloudProvider === "azure") {
      let scopeType = type === "vm" ? "VIRTUAL_MACHINE" :
        type === "storage" ? "STORAGE_BUCKET" : type === "kubernetes" ? "AZURE_KUBERNETES" : "DATABASE";
      let azureVM = userAzureApprovedNotifications?.filter(
        (userAzureNotification: any) =>
          userAzureNotification.scopeType === scopeType &&
          userAzureNotification.status === "APPROVED" &&
          userAzureNotification.resourceName === resource.name
        ,
      );
      return azureVM && azureVM
    } else if (cloudProvider === "gcp") {
      const fetchResourceLevel = userGCPApprovedNotifications?.filter(
        (userGCPNotification: any) =>
          userGCPNotification.accessType === "resources" &&
          userGCPNotification.requestStatus === "APPROVED" &&
          userGCPNotification.accessName === resource.name
      );
      return fetchResourceLevel && fetchResourceLevel
    } else if (cloudProvider === "aws") {
      const fetchResourceLevel = userAWSApprovedNotifications?.filter(
        (userAWSNotification: any) =>
          userAWSNotification.accessType === "resources" &&
          userAWSNotification.requestStatus === "APPROVED" &&
          userAWSNotification.accessName === resource.name
      );
      return fetchResourceLevel && fetchResourceLevel
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "bg-green-500/10 text-green-600 dark:text-green-400";
      case "stopped":
        return "bg-red-500/10 text-red-600 dark:text-red-400";
      case "available":
        return "bg-blue-500/10 text-blue-600 dark:text-blue-400";
      default:
        return "bg-gray-500/10 text-gray-600 dark:text-gray-400";
    }
  };

  const getResourceTypeLabel = (type: string) => {
    const typeInfo = getResourceTypeIcon(type);
    return `${config.label} ${typeInfo.name}`;
  };


  const getUserCloudConfig = async (cloudProvider: string) => {
    if (cloudProvider === "gcp") {
      let response = await getGCPUserConfig(email);
      if (response) {
        let { gcpUserName, gcpAccountEmail, projectId } = response;
        if (gcpAccountEmail) {
          setUserName(gcpUserName)
          setUserEmail(gcpAccountEmail)
          setIsConfigured(true)
          setConfigDialogOpen(false)
          setProjectId(projectId)
        } else {
          setUserName("")
          setUserEmail("")
          setIsConfigured(false)
          setConfigDialogOpen(true)
        }
      } else {

      }
    } else if (cloudProvider === "aws") {
      const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
      const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
      let response = await fetchUserConfiguration(latestTenant, email)
      console.log("AWS User Config Response:", response, Array.isArray(response));
      if (response && Array.isArray(response)) {
        let filteredResponse: any;
        if (selectedAwsAccount) {
          filteredResponse = response.find((item: any) => item.awsAccountId === selectedAwsAccount);
        } else {
          filteredResponse = response[0];
        }
        if (filteredResponse) {
          let { awsUserName, awsAccountARN, awsAccountId } = filteredResponse;
          console.log(awsUserName, awsAccountARN, awsAccountId)
          myLocalStorage.setItem("selectedAwsAccount", awsAccountId);
          setUserName(awsUserName)
          setUserEmail(awsAccountARN)
          setIsConfigured(true)
          setProjectId(awsAccountId)
        } else {
          setUserName("")
          setUserEmail("")
          setIsConfigured(false)
        }
      } else if (response) {
        let { awsUserName, awsAccountARN, awsAccountId } = response;
        console.log(response)

        setUserName(awsUserName)
        setUserEmail(awsAccountARN)
        setIsConfigured(true)
        setProjectId(awsAccountId)
      } else {
        setUserName("")
        setUserEmail("")
        setIsConfigured(false)
      }
    } else {
      try {
        let response = await getAzureUserConfig(email);
        if (response) {
          let { azureId } = response;
          setUserId(azureId);
          setIsConfigured(true)
          setConfigDialogOpen(false)
        } else {
          setIsConfigured(false)
          setConfigDialogOpen(true)
        }
      } catch (e) {
        setIsConfigured(false)
        setConfigDialogOpen(true)
      }

    }
  }

  const submitUserReg = async () => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    try {
      if (cloudProvider === "gcp") {
        let response = await configureGCPUser(latestTenant, email, cloudConfigData?.gcpUsername || "", cloudConfigData?.gcpAccountEmail)
        if (response) {
          setConfigDialogOpen(false);
          await getUserCloudConfig(cloudProvider);
          setRefreshAccountList(prev => prev + 1);
        } else {
          setIsConfigured(false)
        }
      } else if (cloudProvider === "azure") {
        let payload = {
          azureId: cloudConfigData.azureId,
          displayName: cloudConfigData.displayName,
          wsTenantName: latestTenant,
          email: email,
        }
        let response = await configureUser(payload)
        if (response) {
          setConfigDialogOpen(false);
          await getUserCloudConfig(cloudProvider);
          setRefreshAccountList(prev => prev + 1);
        } else {
          setIsConfigured(false)
        }

      } else if (cloudProvider === "aws") {
        const payload = {
          tenantName: latestTenant,
          tenantEmail: email,
          users: cloudConfigData.awsCredentials
        };
        let response = await configureAWSUser(payload)
        if (response) {
          setConfigDialogOpen(false);
          await getUserCloudConfig(cloudProvider);
          setRefreshAccountList(prev => prev + 1);
        } else {
          setIsConfigured(false)
        }
      }


    } catch (e) {
      setIsConfigured(false)
    }


  }




  useEffect(() => {
    const fetchResources = async () => {
      try {
        const resources = await getCurrentProjectResources();
        setCurrentResources(resources);
      } catch (error) {
        console.error("Error fetching project resources:", error);
      }
    };

    fetchResources();
  }, [cloudProvider, selectedTenant, selectedResourceType, cloudProviderChanges]);


  useEffect(() => {

    if (requestAccessDialogOpen) {
      const fetchApplicableRoles = async () => {
        try {
          const resources: any[] = await getApplicableRoles();
          setAvailableRoles(resources);
        } catch (error) {
          console.error("Error fetching project resources:", error);
        }
      };
      fetchApplicableRoles();
    }


  }, [cloudProvider, selectedTenant, requestAccessDialogOpen, selectedResourceType]);




  useEffect(() => {
    if (cloudProvider === "aws") setSelectedResourceType("ec2")
    else setSelectedResourceType("vm")
  }, [cloudProvider])


  useEffect(() => {
    const checkUserConfig = async () => {
      try {
        await getUserCloudConfig(cloudProvider);
      } catch (err) {
        console.error("Error checking user config:", err);
      }
    };
    checkUserConfig();
  }, [cloudProvider, email, cloudProviderChanges, selectedTenant]);


  if (!isConfigured) {
    return (
      <div className="h-full flex flex-col bg-background">
        <div className="border-b bg-card">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                <CloudIcon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">{config.name} Resources</h1>
                <p className="text-sm text-muted-foreground">Request access to {config.fullName} resources</p>
              </div>
            </div>
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <CloudIcon className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No User Configured</h3>
                <p className="text-sm text-muted-foreground max-w-md mb-6">
                  Please configure your {config.name} account first.
                </p>

                {!configDialogOpen && (
                  <Button onClick={() => setConfigDialogOpen(true)}>
                    Configure {config.name}
                  </Button>
                )}

              </CardContent>
            </Card>



            {configDialogOpen && (
              <TenantCloudConfigureDialog
                open={configDialogOpen}
                onOpenChange={setConfigDialogOpen}
                cloudProvider={cloudProvider}
                config={config}
                cloudConfigData={cloudConfigData}
                setCloudConfigData={setCloudConfigData}
                setIsConfigured={setIsConfigured}
                subUserReg={submitUserReg}
              />
            )}
          </div>
        </div>
      </div>

    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
              <CloudIcon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">{config.name} Resources</h1>
              <p className="text-sm text-muted-foreground">Request access to {config.fullName} resources</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            {/* Project Selector (for all clouds) */}
            <div className="flex-1 max-w-xs">
              <label className="text-xs font-medium mb-1.5 block text-muted-foreground">
                {cloudProvider === "gcp"
                  ? "Project"
                  : cloudProvider === "azure"
                    ? "Subscription"
                    : "Account"}
              </label>
              <CloudAccountList
                key={refreshAccountList}
                cloudProvider={cloudProvider}
                onSelectorChange={handleCloudProvider}
                isLabelNeeded={false}
                isAdmin={false}
              />
            </div>

            {/* User Access Details - Compact Card */}
            {userAccess && (
              <Card className="flex-1" data-testid="card-user-access">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-semibold text-sm">{userAccess.userName || "N/A"}</div>
                          <div className="text-xs text-muted-foreground">{userAccess.accountEmail}</div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Synced: {userAccess.syncedAt}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Roles:</span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {(showAllRoles ? userAccess.roles : userAccess.roles.slice(0, 3)).map((role, index) => (
                          <Badge key={index} variant="secondary" className="font-mono text-xs">
                            {role}
                          </Badge>
                        ))}
                        {userAccess.roles.length > 3 && (
                          <button
                            className="text-xs text-primary hover:underline cursor-pointer"
                            onClick={() => setShowAllRoles(!showAllRoles)}
                            data-testid="button-toggle-roles"
                          >
                            {showAllRoles ? 'Show less' : `+${userAccess.roles.length - 3} more`}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Resource Types */}
        <div className="w-64 border-r bg-card flex flex-col flex-shrink-0">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search types..."
                value={typeSearchQuery}
                onChange={(e) => setTypeSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="input-search-resource-types"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto p-2">
            <div className="space-y-1">


              {/* Resource Types */}
              {filteredResourceTypes.map((type) => {
                const count = currentResources.filter((r) => r.type === type.id).length;
                const Icon = type.icon;
                const isSelected = selectedResourceType === type.id;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleFilterChange(type.id)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${isSelected
                      ? "bg-primary text-primary-foreground"
                      : "hover-elevate active-elevate-2"
                      }`}
                    data-testid={`button-filter-${type.id}`}
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="font-medium truncate">{type.name}</span>
                    </div>
                    <Badge variant="secondary" className={isSelected ? "bg-primary-foreground/20 text-primary-foreground" : ""}>
                      {count}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {/* Search and Stats */}
            <div className="mb-6 flex items-center justify-between gap-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search resources by name or region..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-10"
                    data-testid="input-search-resources"
                  />
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium text-foreground">{paginatedResources.length}</span> of{" "}
                <span className="font-medium text-foreground">{filteredResources.length}</span> resources
              </div>
            </div>

            {/* Resources Grid */}
            {filteredResources.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {searchQuery ? "No resources match your search" : "No resources found"}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {paginatedResources.map((resource) => {
                    const resourceTypeInfo = getResourceTypeIcon(resource.type);
                    const Icon = resourceTypeInfo.icon;
                    const isHasAccess = fetchAccessGranted(selectedResourceType || "", resource);
                    return (
                      <Card
                        key={resource.id}
                        className="hover-elevate transition-all flex flex-col"
                        data-testid={`card-resource-${resource.id}`}
                      >
                        <CardHeader className="pb-4">
                          <div className="flex items-start justify-between gap-2">
                            <div
                              className={`w-12 h-12 rounded-md ${resourceTypeInfo.color} flex items-center justify-center flex-shrink-0`}
                            >
                              <Icon className="w-6 h-6" />
                            </div>
                            <Badge
                              variant="secondary"
                              className={`text-xs ${getStatusColor(resource.status)}`}
                            >
                              {resource.status}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="flex flex-col flex-1">
                          <div className="flex-1 space-y-4">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-sm break-all flex-1" title={resource.name}>
                                  {resource.name}
                                </h3>
                                <DetailsDialog
                                  data={resource}
                                  title={`${resource.name} Details`}
                                  trigger="icon"
                                  testId={`button-info-${resource.id}`}
                                />
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {resource.description}
                              </p>
                            </div>

                            <div className="space-y-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Region:</span>
                                <span className="font-medium">{resource.region}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Created:</span>
                                <span className="font-medium">{resource.createdAt}</span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 pt-4 border-t">
                            {isHasAccess && isHasAccess.length > 0 ? (
                              <div className="flex items-center justify-center gap-2 p-3 rounded-md bg-green-500/10 border border-green-500/20">
                                <Clock className="w-4 h-4 text-green-600 dark:text-green-400" />
                                <div className="flex flex-col items-center">
                                  <span className="text-xs text-muted-foreground">Access Granted</span>
                                  <span className="text-sm font-semibold text-green-600 dark:text-green-400 font-mono">
                                    {formatTime(isHasAccess, cloudProvider)}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <Button
                                className="w-full gap-2"
                                size="sm"
                                onClick={() => {
                                  setRequestAccessResource(resource);
                                  setRequestAccessDialogOpen(true);
                                }}
                                data-testid={`button-request-access-${resource.id}`}
                              >
                                Request Access
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t pt-4">
                    <div className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        data-testid="button-page-first"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        data-testid="button-page-prev"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              className="w-9"
                              data-testid={`button-page-${pageNum}`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        data-testid="button-page-next"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        data-testid="button-page-last"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Request Access Dialog */}
      {requestAccessResource &&
        <RequestCloudAccessDialog
          open={requestAccessDialogOpen}
          onOpenChange={setRequestAccessDialogOpen}
          resource={requestAccessResource}
          resourceName={requestAccessResource?.name || ""}
          resourceType={cloudProvider}
          resourceTypeLabel={requestAccessResource ? getResourceTypeLabel(requestAccessResource.type) : undefined}
          availableRoles={availableRoles}
          from={selectedResourceType || ""}
          userId={userId}
          userEmail={userEmail}
          userName={userName}
        />}


    </div>
  );
}
